import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FormattedMessage, useIntl } from 'react-intl';
import { LazyLoadImage } from 'react-lazy-load-image-component';

import { RATE_TYPES } from '@ratehub/documents/mtg-journey-document/stable';
import {
    ANALYTICS_KEY,
    PageBehaviourButton,
    Colours,
    Sizes,
    IconExclamationMark,
    CircleSpinner,
    useCondensedTheme,
    getFormattedNumber,
    ImageSizesByType,
} from '@ratehub/base-ui';

import isProviderRate from '../functions/isProviderRate';


function MortgageRateCardSimple({ rate, hasError, onClick, className, isLoading, isButtonDisabled, ...otherProps }) {
    const isCompact = useCondensedTheme();
    const intl = useIntl();

    return (
        <Container
            ref={self => self && (self[ANALYTICS_KEY] = rate)}
            // position: relative is needed for the loading spinner
            className={classNames(className, 'rh-display-flex rh-position-relative rh-border-width-1px rh-border-style-solid rh-py-0_5', { 
                'rh-border-color-stone': !rate,
                'rh-border-color-blueberry-dark': !!rate,
                'rh-px-1': !isCompact,
                'rh-px-0_25': isCompact,
            })}
            {...otherProps}
        >
            <If condition={isLoading}>
                <CircleSpinner />
            </If>

            <Choose>
                <When condition={!hasError}>
                    <div className={classNames('rate-information rh-display-flex flex-1', { 
                        'full-width': isCompact, 
                        'column': !isCompact, 
                    })}
                    >
                        <p className={classNames('rh-mx-auto rh-my-0 aligned-text', { 
                            'disabled rh-fg-stone': !rate,
                            'rh-title-s': isCompact,
                            'rh-title-2xl': !isCompact,
                            'rh-fg-blackberry': !!rate && isCompact,
                            'rh-fg-blueberry-dark': !!rate && !isCompact,
                        })}
                        >
                            {getFormattedNumber(rate?.value / 100, 'percent2', intl)}
                        </p>

                        <If condition={rate}>
                            <p className={classNames('rh-title-3xs weight-regular rh-my-0 rh-mx-auto aligned-text', {
                                'rh-fg-blackberry': isCompact,
                                'rh-fg-blueberry-dark': !isCompact,
                            })}
                            >
                                <Choose>
                                    <When condition={isProviderRate(rate) && isCompact}>
                                        <FormattedMessage
                                            id="mtg-calculators.RateCard.termTypeLabel"
                                            defaultMessage="{term}-yr/{isFixedType, select, true {fix} other {var}}"
                                            values={{
                                                term: rate?.term / 12,
                                                isFixedType: rate?.type === RATE_TYPES.FIXED,
                                            }}
                                        />
                                    </When>
                                    <When condition={isProviderRate(rate)}>
                                        <FormattedMessage
                                            id="mtg-calculators.RateCard.termTypeLabel-expanded"
                                            defaultMessage="{term}-year {isFixedType, select, true {Fixed} other {Variable}}"
                                            values={{
                                                term: rate?.term / 12,
                                                isFixedType: rate?.type === RATE_TYPES.FIXED,
                                            }}
                                        />
                                    </When>
                                    <Otherwise>
                                        <FormattedMessage
                                            id="mtg-common.RateCard.customRateLabel"
                                            defaultMessage="Custom"
                                        />
                                    </Otherwise>
                                </Choose>
                            </p>
                        </If>
                    </div>
                    <If condition={!!rate}>
                        <div 
                            className="provider-information rh-display-flex flex-1"
                        >
                            <Choose>
                                <When condition={isProviderRate(rate)}>
                                    <LazyLoadImage
                                        className={classNames('provider-logo rh-height-auto rh-box-sizing-border-box', {
                                            'compact': isCompact,
                                        })}
                                        src={rate.provider.logo}
                                        alt={rate.provider.name}
                                        height={ImageSizesByType.PROVIDERS.BADGE_SMALL.HEIGHT}
                                        width={ImageSizesByType.PROVIDERS.BADGE_SMALL.WIDTH}
                                    />
                                    <p
                                        className={classNames('provider-name rh-my-0 weight-medium rh-fg-blackberry rh-text-align-center', {
                                            'rh-text-xs': isCompact,
                                            'rh-text-m rh-ml-0_25': !isCompact,
                                        })}
                                    >
                                        {rate.provider.name}
                                    </p>
                                </When>
                                <Otherwise>
                                    <div className="rh-display-flex flex-1">
                                        <div
                                            className={classNames({ // empty placeholder to provide consistent height for custom rate
                                                'rh-icon-xs': isCompact,
                                                'rh-icon-s': !isCompact,
                                            })}
                                        />
                                    </div>
                                </Otherwise>
                            </Choose>
                        </div>
                    </If>

                    <PageBehaviourButton
                        data-name="mtg-calculators-MortgageRateCardSimple-cta"
                        className={classNames('button flex-1', { 
                            'full-width': !rate || isCompact,
                        })}
                        message={(
                            <FormattedMessage
                                id="mtg-common.MortgageRateCardSimple.cta"
                                defaultMessage="Customize"
                            />
                        )}
                        onClick={onClick}
                        disabled={isButtonDisabled || !rate}
                        size={isCompact ? 'extra-small' : 'small'}
                    />
                </When>

                <Otherwise>
                    <div className="errorContainer rh-display-flex full-width">
                        <IconExclamationMark
                            className="rh-icon-xs"
                            outlineWidth="0"
                            stroke={Colours.COCONUT}
                            fill={Colours.STRAWBERRY_DARK}
                        />

                        <p className="rh-text-s rh-mb-0 rh-text-align-center">
                            <FormattedMessage
                                id="mtg-calculators.RateCard.errorMessage"
                                defaultMessage="No rates available. Please adjust your input and try again"
                            />
                        </p>
                    </div>
                </Otherwise>
            </Choose>
        </Container>
    );
}

MortgageRateCardSimple.propTypes = {
    rate: PropTypes.object,
    hasError: PropTypes.bool,
    isLoading: PropTypes.bool,
    isButtonDisabled: PropTypes.bool,
    onClick: PropTypes.func,
    className: PropTypes.string,
};

MortgageRateCardSimple.defaultProps = {
    rate: undefined,
    hasError: false,
    isLoading: false,
    isButtonDisabled: false,
    onClick: undefined,
    className: undefined,
};

const Container = styled.div`
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0 ${Sizes.SPACING.ONE};

    .flex-1 {
        flex: 1;
    }

    /* If we don't have the rate, we want to align things to the center */
    /* NOTE: This MUST appear after .flex-1 because this should override it */
    .full-width {
        flex: 100% 0 0;
    }

    > .rate-information {
        flex-wrap: wrap;
        justify-content: center;
        column-gap: ${Sizes.SPACING.HALF};

        &.column {
            flex-direction: column;
        }
        
        .aligned-text {
            align-self: baseline;
        }
    }

    > .provider-information {
        align-self: center;
        justify-content: center;
        align-items: center;
        flex-wrap: nowrap;

        > .provider-logo {
            object-fit: contain;
            flex-shrink: 0;
            height: auto;
            width: ${Sizes.ICONS.S};

            .compact {
                width: ${Sizes.ICONS.XS};
            }
        }

        > .provider-name {
            line-height: 1.4;
            white-space: break-spaces;
        }
    }

    > .button {
        justify-content: center;
    }

    > .errorContainer {
        align-self: center;
        align-items: center;
        flex-direction: column;
    }
`;

export default observer(MortgageRateCardSimple);
